import React, { useEffect, useState } from "react";
import { Eye } from "lucide-react";
import { api } from "@/lib/api";

const VISIT_KEY = "cs:lastVisitPingAt";
const TWENTY_FOUR_HRS = 24 * 60 * 60 * 1000;

/**
 * Site-wide visitor counter shown in the footer. Each browser pings the
 * /stats/visit endpoint at most once per 24h, so the total reflects unique
 * daily visitors rather than page refreshes.
 */
export default function VisitorCounter({ variant = "footer" }) {
  const [visits, setVisits] = useState(null);

  useEffect(() => {
    let active = true;
    const run = async () => {
      try {
        const last = parseInt(localStorage.getItem(VISIT_KEY) || "0", 10);
        const shouldPing = !last || Date.now() - last > TWENTY_FOUR_HRS;
        if (shouldPing) {
          const { data } = await api.post("/stats/visit");
          if (!active) return;
          setVisits(data);
          try { localStorage.setItem(VISIT_KEY, String(Date.now())); } catch { /* ignore */ }
        } else {
          const { data } = await api.get("/stats/visits");
          if (!active) return;
          setVisits(data);
        }
      } catch { /* ignore — keep silent if offline */ }
    };
    run();
    return () => { active = false; };
  }, []);

  if (!visits || visits.total_visits <= 0) return null;

  const total = visits.total_visits.toLocaleString();
  const today = visits.today_visits.toLocaleString();

  if (variant === "inline") {
    return (
      <div
        data-testid="visitor-counter-inline"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass border border-amber-500/15 text-xs text-slate-400"
      >
        <Eye className="w-3 h-3 text-amber-300/80" />
        <span>
          <span className="text-amber-300 font-semibold" data-testid="visitor-counter-total">{total}</span> visitors
          <span className="text-slate-500"> · {today} today</span>
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid="visitor-counter"
      className="flex items-center justify-center gap-2 text-[11px] text-slate-500"
    >
      <Eye className="w-3 h-3 text-amber-300/70" />
      <span>
        <span data-testid="visitor-counter-total" className="text-amber-300/90 font-semibold">{total}</span>{" "}
        cosmic visitors
        <span className="hidden sm:inline">
          {" · "}
          <span data-testid="visitor-counter-today" className="text-slate-400">{today}</span>
          {" today"}
        </span>
      </span>
    </div>
  );
}
